import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Card from "components/card";
import Toast from "components/cms/Toast";
import { getErrorMessage } from "utils/cms";
import { deleteEnquiry, listEnquiries } from "api/enquiry";

const LIMIT = 20;

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function EnquiryList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [toast, setToast] = useState(null);

  const load = useCallback(async (pageNum) => {
    setLoading(true);
    setError("");
    try {
      const res = await listEnquiries(pageNum, LIMIT);
      const data = res.data?.data || {};
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [load, page]);

  const onDelete = async (item) => {
    const label = item.name || item.email || "this enquiry";
    if (!window.confirm(`Delete enquiry from ${label}? This cannot be undone.`)) {
      return;
    }
    setDeletingId(item._id);
    try {
      const res = await deleteEnquiry(item._id);
      setToast({
        type: "success",
        message: res.data?.message || "Enquiry deleted successfully",
        id: Date.now(),
      });
      if (items.length === 1 && page > 1) {
        setPage((value) => value - 1);
      } else {
        await load(page);
      }
    } catch (err) {
      setToast({
        type: "error",
        message: getErrorMessage(err),
        id: Date.now(),
      });
    } finally {
      setDeletingId("");
    }
  };

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="mt-3">
      <Toast toast={toast} onClose={() => setToast(null)} />
      <Card extra="overflow-hidden rounded-2xl border border-gray-200 shadow-none dark:!border-white/10">
        <div className="flex items-center justify-between border-b border-purple-100 bg-gradient-to-r from-[#F3F0FF] to-white px-4 py-3 dark:border-white/10 dark:from-navy-800 dark:to-navy-800">
          <div>
            <h3 className="text-base font-bold text-navy-700 dark:text-white">
              Enquiries
            </h3>
            <p className="text-xs text-gray-500">
              {total} submission{total === 1 ? "" : "s"} from the contact form
            </p>
          </div>
        </div>

        {error ? (
          <p className="px-4 py-6 text-sm text-red-500">{error}</p>
        ) : loading ? (
          <p className="px-4 py-6 text-sm text-gray-500">Loading enquiries…</p>
        ) : items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500">
            No enquiries yet. Submissions from the website contact form will
            appear here.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-b border-gray-100 text-[11px] font-bold uppercase tracking-[0.12em] text-[#6A4CF0] dark:border-white/10">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3 text-right"> </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item._id}
                    className="border-b border-gray-50 text-sm text-navy-700 last:border-0 dark:border-white/5 dark:text-white"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                      {formatDate(item.createdAt)}
                    </td>
                    <td className="px-4 py-3 font-medium">{item.name || "—"}</td>
                    <td className="px-4 py-3">{item.email || "—"}</td>
                    <td className="px-4 py-3">{item.phone || "—"}</td>
                    <td className="max-w-[220px] truncate px-4 py-3 text-gray-600 dark:text-gray-300">
                      {item.reason || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Link
                          to={`/admin/enquiries/${item._id}`}
                          className="inline-flex rounded-lg bg-gradient-to-r from-[#4C3FE8] to-[#A855F7] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
                        >
                          View
                        </Link>
                        <button
                          type="button"
                          disabled={deletingId === item._id}
                          onClick={() => onDelete(item)}
                          className="inline-flex rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10"
                        >
                          {deletingId === item._id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > LIMIT ? (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm dark:border-white/10">
            <p className="text-gray-500">
              Page {page} of {pages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((value) => value - 1)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium disabled:opacity-40 dark:border-white/10"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= pages}
                onClick={() => setPage((value) => value + 1)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium disabled:opacity-40 dark:border-white/10"
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
